import React from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Fade,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AppButton from "../AppButton";
import theme from "../../utils/theme";

export const modalStyles = {
  dialogPaper: {
    borderRadius: 3,
    bgcolor: theme.palette.background.default,
    boxShadow: 10,
    width: 960,
    maxWidth: "98vw",
    maxHeight: "99vh",
    mt: 8,
  },
  dialogTitle: {
    bgcolor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 700,
    fontSize: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    pr: 1.5,
    minHeight: 56,
  },
  dialogContent: {
    maxHeight: "75vh",
    overflowY: "auto",
    overflowX: "hidden",
    p: "1rem",
  },
  closeButton: {
    color: "#fff",
    "&:hover": {
      bgcolor: "rgba(255, 255, 255, 0.1)",
    },
  },
  dialogTitleText: {
    color: "#fff",
    fontWeight: 700,
    fontSize: 20,
  },
};

const ModalTemplate = ({
  open,
  onClose,
  title,
  titleIcon,
  children,
  actions,
  showActions = true,
  primaryButtonLabel = "Save",
  secondaryButtonLabel = "Close",
  onPrimaryAction,
  onSecondaryAction,
  isPrimaryLoading = false,
  showCloseButton = true,
  maxWidth = "md",
  fullWidth = true,
  customPaperProps,
  customContentProps,
  footerNote,
  ...dialogProps
}) => {
  const handleSecondaryAction = () => {
    if (onSecondaryAction) {
      onSecondaryAction();
    } else {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      TransitionComponent={Fade}
      BackdropProps={{ style: { background: "rgba(40, 0, 0, 0.5)" } }}
      PaperProps={{
        sx: {
          ...modalStyles.dialogPaper,
          ...customPaperProps,
        },
      }}
      {...dialogProps}
    >
      <DialogTitle sx={modalStyles.dialogTitle}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {titleIcon &&
            React.cloneElement(titleIcon, {
              fontSize: "small",
              sx: { color: "#fff", mb: "2px" },
            })}
          <span style={modalStyles.dialogTitleText}>{title}</span>
        </Box>
        {showCloseButton && (
          <Tooltip title="Close Dialog" arrow placement="top">
            <IconButton
              aria-label="close"
              onClick={onClose}
              size="small"
              sx={modalStyles.closeButton}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </DialogTitle>

      <DialogContent
        sx={{
          ...modalStyles.dialogContent,
          ...customContentProps,
        }}
      >
        {children}
      </DialogContent>

      {showActions && actions ? (
        <DialogActions sx={{ p: "1rem" }}>{actions}</DialogActions>
      ) : showActions && (onPrimaryAction || onSecondaryAction) ? (
        <DialogActions sx={{ p: "1rem" }}>
          <AppButton
            buttonType="greyed"
            onClick={handleSecondaryAction}
            sx={{ mx: 1 }}
            label={secondaryButtonLabel}
          />
          {onPrimaryAction && (
            <AppButton
              buttonType="primary"
              onClick={onPrimaryAction}
              sx={{ minWidth: 120, mx: 1 }}
              label={primaryButtonLabel}
              isLoading={isPrimaryLoading}
            />
          )}
        </DialogActions>
      ) : footerNote ? (
        <Typography
          variant="caption"
          sx={{ p: "1rem", color: theme.palette.primary.main }}
        >
          <b>Note:</b> {footerNote}
        </Typography>
      ) : null}
    </Dialog>
  );
};

export default ModalTemplate;
